/**
 * net/screens.ts — o que o GESTOR faz com telas (API-CONTRACT §3.7/§3.8).
 *
 * Só o necessário para a Fase B (plano §3.3): virar este navegador em tela,
 * listar e ler a cota do plano. Renomear/orientar/revogar entram com o
 * gerenciador (Fase M) — não se adivinha endpoint agora.
 *
 * `Idempotency-Key`: decisão do mestre (HYBRID §12.2) — o backend aceita e
 * deduplica, o web envia. Sem ela, duplo clique/retry criaria DUAS telas e
 * queimaria cota — em conta Free (1 tela) isso é tijolo, não incômodo.
 */
import { ApiError } from './apiFetch';
import type { ScreenCredentials } from './credentials';
import type { Session } from './session';

export interface ScreenSummary {
  id: string;
  name: string;
  platform: 'android' | 'web' | 'androidtv';
  status: 'online' | 'offline';
  lastSeenAt?: string;
  pairedAt?: string;
  orientation: 'landscape' | 'portrait';
}

export interface AccountPlan {
  planName: string;
  maxScreens: number;
  currentScreens: number;
  upgradeUrl: string | null;
  allowVideo: boolean;
  maxMediaItems: number;
  maxDurationSeconds: number;
}

export interface SelfRegisterResult {
  screen: ScreenSummary;
  credentials: ScreenCredentials;
}

export interface ScreensOptions {
  now?: () => Date;
}

/** Chave nova por TENTATIVA de vínculo; reenviada em retry/double clique. */
function newIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Nome padrão quando o lojista não escreve nada (HYBRID §12.5). */
export function suggestScreenName(existing: number): string {
  return `Navegador ${existing + 1}`;
}

export class ScreensClient {
  private readonly session: Session;
  private readonly now: () => Date;

  /** O TRANSPORTE é o da `Session` (é ela que tem o access e o refresh-no-401). */
  constructor(session: Session, options: ScreensOptions = {}) {
    this.session = session;
    this.now = options.now ?? (() => new Date());
  }

  /**
   * `POST /screens/self-register` — este navegador vira tela da conta do access.
   * O device token volta na resposta (§3.7) e NUNCA é logado.
   *
   * `idempotencyKey` (opcional) é a chave da TENTATIVA: o chamador gera uma ao
   * começar e reusa a MESMA em retry/duplo clique. Sem isso, um retry depois de
   * uma resposta perdida criaria uma SEGUNDA tela — em conta Free isso é a cota
   * queimada. O refresh-no-401 do `call()` já preserva os headers desta chamada;
   * esta chave cobre o retry do CHAMADOR.
   */
  async selfRegister(
    input: { name: string; orientation?: 'landscape' | 'portrait' },
    idempotencyKey: string = newIdempotencyKey(),
  ): Promise<SelfRegisterResult> {
    const body = await this.session.call<{ screen: ScreenSummary; deviceToken: string }>({
      method: 'POST',
      path: '/screens/self-register',
      body: {
        name: input.name,
        platform: 'web',
        ...(input.orientation ? { orientation: input.orientation } : {}),
      },
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    if (!body?.screen?.id || typeof body.deviceToken !== 'string' || body.deviceToken === '') {
      throw new ApiError('BAD_RESPONSE', 'Resposta de self-register sem deviceToken.', 200);
    }
    return {
      screen: body.screen,
      credentials: {
        deviceToken: body.deviceToken,
        screenId: body.screen.id,
        pairedAt: this.now().toISOString(),
        ...(body.screen.name ? { screenName: body.screen.name } : {}),
      },
    };
  }

  /**
   * `POST /screens/:id/credential/revoke` (204, §3.8) — mata o device token sem
   * remover a tela do inventário. É o caminho para desfazer um vínculo errado.
   */
  async revokeCredential(screenId: string): Promise<void> {
    await this.session.call<null>({ method: 'POST', path: `/screens/${encodeURIComponent(screenId)}/credential/revoke` });
  }

  /** `GET /screens` — inventário da conta (usado na tela "escolher"). */
  async list(): Promise<ScreenSummary[]> {
    const body = await this.session.call<{ screens?: ScreenSummary[] }>({ method: 'GET', path: '/screens' });
    return Array.isArray(body?.screens) ? body.screens : [];
  }

  /** `GET /account/plan` — cota para a tela de escolha e o paywall. */
  async getAccountPlan(): Promise<AccountPlan> {
    return this.session.call<AccountPlan>({ method: 'GET', path: '/account/plan' });
  }
}
